<template>
  <div class="space-y-6 animate-fade-in">
    <!-- Invite User Card -->
    <div class="glass rounded-xl p-6">
      <h3 class="text-lg font-semibold text-white mb-2">Invite User to TestDrive</h3>
      <p class="text-slate-400 text-sm mb-4">
        Send a Supabase Auth invitation. The user will receive an email with a sign-in link.
      </p>
      <div class="grid grid-cols-1 md:grid-cols-6 gap-3 items-start">
        <input
          v-model="email"
          type="email"
          placeholder="Email *"
          required
          class="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
        <input
          v-model="firstName"
          type="text"
          placeholder="First name *"
          required
          class="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500"
        />
        <input
          v-model="lastName"
          type="text"
          placeholder="Last name *"
          required
          class="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500"
        />
        <div>
          <input
            v-model="mobile"
            type="tel"
            placeholder="+15551234567 *"
            required
            class="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500"
          />
          <p class="text-[10px] text-slate-500 mt-0.5">E.164: +1 then 10 digits</p>
        </div>
        <select
          v-model="userRole"
          class="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
        >
          <option value="admin">Admin</option>
          <option value="sales_engineer">Sales Engineer</option>
          <option value="lingo_partner_support">Partner Support</option>
          <option value="lingo_sales_support">Sales Support</option>
          <option value="partner">Partner</option>
          <option value="viewer">Viewer</option>
        </select>
        <button
          type="button"
          @click="sendInvite"
          class="px-4 py-2 rounded-lg btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="!canInvite || inviting"
        >
          {{ inviting ? 'Sending...' : 'Send Invite' }}
        </button>
      </div>
      <p v-if="!canInvite && (email || firstName || lastName || mobile)" class="text-xs text-amber-400 mt-2">
        All fields (Email, First name, Last name, Mobile) are required.
      </p>
      <p
        v-if="message"
        class="text-sm mt-3 p-3 rounded-lg"
        :class="messageType === 'error' ? 'bg-red-900/30 text-red-300' : 'bg-green-900/30 text-green-300'"
        v-html="message"
      ></p>
    </div>

    <!-- Users List -->
    <div class="glass rounded-xl p-6">
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-lg font-semibold text-white">Users</h3>
        <button
          @click="loadUsers"
          class="text-sm text-slate-400 hover:text-white transition-colors"
          :disabled="usersLoading"
        >
          {{ usersLoading ? 'Loading...' : '↻ Refresh' }}
        </button>
      </div>

      <div v-if="usersLoading && !users.length" class="text-slate-400 text-sm py-4">
        Loading users...
      </div>
      <div v-else-if="usersError" class="text-red-400 text-sm py-4">{{ usersError }}</div>
      <div v-else-if="!users.length" class="text-slate-500 text-sm py-4">
        No users found. Send an invitation above.
      </div>
      <table v-else class="w-full text-left">
        <thead>
          <tr class="border-b border-slate-700">
            <th class="pb-2 text-sm font-medium text-slate-400">Email</th>
            <th class="pb-2 text-sm font-medium text-slate-400">First</th>
            <th class="pb-2 text-sm font-medium text-slate-400">Last</th>
            <th class="pb-2 text-sm font-medium text-slate-400">Mobile</th>
            <th class="pb-2 text-sm font-medium text-slate-400">Status</th>
            <th class="pb-2 text-sm font-medium text-slate-400">Last Login</th>
            <th class="pb-2 text-sm font-medium text-slate-400 w-24"></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="u in users"
            :key="u.user_id || u.id"
            class="border-b border-slate-800 hover:bg-slate-800/50 transition-colors"
          >
            <!-- Editing mode -->
            <template v-if="editingUserId === (u.user_id || u.id)">
              <td class="py-2 text-sm text-white">{{ u.email }}</td>
              <td class="py-1">
                <input
                  v-model="editForm.first_name"
                  class="w-full px-2 py-1 bg-slate-800 border border-slate-600 rounded text-sm text-white"
                />
              </td>
              <td class="py-1">
                <input
                  v-model="editForm.last_name"
                  class="w-full px-2 py-1 bg-slate-800 border border-slate-600 rounded text-sm text-white"
                />
              </td>
              <td class="py-1">
                <input
                  v-model="editForm.mobile"
                  type="tel"
                  placeholder="+15551234567"
                  class="w-full px-2 py-1 bg-slate-800 border border-slate-600 rounded text-sm text-white"
                />
              </td>
              <td class="py-2">
                <span
                  class="text-xs px-2 py-0.5 rounded-full"
                  :class="
                    u.email_verified
                      ? 'bg-green-900/40 text-green-400'
                      : 'bg-amber-900/40 text-amber-400'
                  "
                >
                  {{ u.email_verified ? 'Verified' : 'Pending' }}
                </span>
              </td>
              <td class="py-2 text-sm text-slate-400">{{ formatDate(u.last_login) }}</td>
              <td class="py-1 text-right space-x-1">
                <button
                  @click="saveUser(u)"
                  :disabled="editSaving"
                  class="px-2 py-1 text-xs rounded bg-green-600/30 text-green-400 hover:bg-green-600/50 transition-colors disabled:opacity-50"
                >
                  {{ editSaving ? '…' : 'Save' }}
                </button>
                <button
                  @click="cancelEdit"
                  class="px-2 py-1 text-xs rounded bg-slate-700/50 text-slate-400 hover:bg-slate-700 transition-colors"
                >
                  Cancel
                </button>
              </td>
            </template>
            <!-- Read-only mode -->
            <template v-else>
              <td class="py-2 text-sm text-white">{{ u.email }}</td>
              <td class="py-2 text-sm text-slate-300">
                {{ u.given_name || u.user_metadata?.first_name || '' }}
              </td>
              <td class="py-2 text-sm text-slate-300">
                {{ u.family_name || u.user_metadata?.last_name || '' }}
              </td>
              <td class="py-2 text-sm text-slate-400">
                {{ u.user_metadata?.mobile || u.phone_number || '—' }}
              </td>
              <td class="py-2">
                <span
                  class="text-xs px-2 py-0.5 rounded-full"
                  :class="
                    u.email_verified
                      ? 'bg-green-900/40 text-green-400'
                      : 'bg-amber-900/40 text-amber-400'
                  "
                >
                  {{ u.email_verified ? 'Verified' : 'Pending' }}
                </span>
              </td>
              <td class="py-2 text-sm text-slate-400">{{ formatDate(u.last_login) }}</td>
              <td class="py-2 text-right">
                <button
                  @click="startEdit(u)"
                  class="px-2 py-1 text-xs rounded bg-slate-700/50 text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
                >
                  Edit
                </button>
              </td>
            </template>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { createLogger } from '@/lib/logger'
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient'

const log = createLogger('UsersAuth0')

defineOptions({
  name: 'UsersAuth0View',
})

// ─── Invite form state ──────────────────────────────────────
const email = ref('')
const firstName = ref('')
const lastName = ref('')
const mobile = ref('')
const userRole = ref('sales_engineer')
const message = ref('')
const messageType = ref<'success' | 'error'>('success')
const inviting = ref(false)

/** All required fields must be filled before the invite button enables. */
const canInvite = computed(() =>
  email.value.trim().length > 0 &&
  firstName.value.trim().length > 0 &&
  lastName.value.trim().length > 0 &&
  mobile.value.trim().length > 0,
)

// ─── Users list state ───────────────────────────────────────
const users = ref<any[]>([])
const usersLoading = ref(false)
const usersError = ref('')

// ─── Inline edit state ──────────────────────────────────────
const editingUserId = ref<string | null>(null)
const editForm = ref({ first_name: '', last_name: '', mobile: '' })
const editSaving = ref(false)

/**
 * Retrieve the Auth0 bearer token stored by the auth0 store.
 * Falls back to the ID token if the access token is missing.
 */
function getAuthToken(): string | null {
  return (window as any).__auth0Token || (window as any).__auth0IdToken || null
}

/**
 * Send an invitation through the admin-invite-user Edge Function.
 * All four fields (email, first name, last name, mobile) are required.
 */
async function sendInvite() {
  if (!canInvite.value) {
    message.value = 'All fields (Email, First name, Last name, Mobile) are required.'
    messageType.value = 'error'
    return
  }

  inviting.value = true
  message.value = ''

  try {
    const token = getAuthToken()
    if (!token) {
      throw new Error('Not authenticated — sign out and back in, then retry.')
    }

    const inviteEmail = email.value.trim().toLowerCase()
    log.debug('Sending invite to:', inviteEmail)

    const { data, error: fnError } = await invokeEdgeFunction('admin-invite-user', {
      email: inviteEmail,
      first_name: firstName.value.trim(),
      last_name: lastName.value.trim(),
      mobile: mobile.value.trim(),
      role: userRole.value,
      token,
    })

    if (fnError) {
      throw new Error(fnError)
    }
    if (data && (data as any).error) {
      throw new Error((data as any).error)
    }

    message.value = `✅ Invitation sent to <strong>${inviteEmail}</strong>`
    messageType.value = 'success'

    // Clear form
    email.value = ''
    firstName.value = ''
    lastName.value = ''
    mobile.value = ''
    userRole.value = 'sales_engineer'

    // Dispatch toast
    try {
      window.dispatchEvent(
        new CustomEvent('toast', {
          detail: { type: 'success', message: `Invitation sent to ${inviteEmail}` },
        }),
      )
    } catch {}

    // Refresh user list after invite
    await loadUsers()
  } catch (error) {
    log.error('Invite error:', error)
    message.value = `❌ ${error instanceof Error ? error.message : String(error)}`
    messageType.value = 'error'

    try {
      window.dispatchEvent(
        new CustomEvent('toast', {
          detail: { type: 'error', message: 'Invitation failed' },
        }),
      )
    } catch {}
  } finally {
    inviting.value = false
  }
}

/**
 * Load user list from Auth0 via the auth0-management-users Edge Function.
 */
async function loadUsers() {
  usersLoading.value = true
  usersError.value = ''

  try {
    const token = getAuthToken()
    if (!token) {
      usersError.value = 'Not authenticated'
      return
    }

    const { data, error: fnError } = await invokeEdgeFunction('auth0-management-users', {
      page: 0,
      per_page: 50,
      token,
    })

    if (fnError) {
      throw new Error(fnError)
    }

    // The edge function returns either { users: [...] } or an array directly
    const payload = data as any
    users.value = Array.isArray(payload) ? payload : payload?.users || []
    log.debug('Loaded users:', users.value.length)
  } catch (error) {
    log.error('Load users error:', error)
    usersError.value = error instanceof Error ? error.message : String(error)
  } finally {
    usersLoading.value = false
  }
}

// ─── Inline edit helpers ────────────────────────────────────

/**
 * Begin editing a user row — populate the edit form with current values.
 */
function startEdit(u: any) {
  editingUserId.value = u.user_id || u.id
  editForm.value = {
    first_name: u.given_name || u.user_metadata?.first_name || '',
    last_name: u.family_name || u.user_metadata?.last_name || '',
    mobile: u.user_metadata?.mobile || u.phone_number || '',
  }
}

function cancelEdit() {
  editingUserId.value = null
}

/**
 * Save the edited user profile — updates both Auth0 (via Management API) and
 * the local admin_users table (for phone pre-fill in alert preferences).
 */
async function saveUser(u: any) {
  editSaving.value = true
  try {
    const token = getAuthToken()
    if (!token) throw new Error('Not authenticated')

    const { data, error: fnError } = await invokeEdgeFunction('auth0-update-user', {
      user_id: u.user_id || u.id,
      first_name: editForm.value.first_name.trim(),
      last_name: editForm.value.last_name.trim(),
      mobile: editForm.value.mobile.trim(),
      token,
    })

    if (fnError) throw new Error(fnError)
    if (data && (data as any).error) throw new Error((data as any).error)

    // Update local user object in place
    u.given_name = editForm.value.first_name.trim()
    u.family_name = editForm.value.last_name.trim()
    if (!u.user_metadata) u.user_metadata = {}
    u.user_metadata.first_name = editForm.value.first_name.trim()
    u.user_metadata.last_name = editForm.value.last_name.trim()
    u.user_metadata.mobile = editForm.value.mobile.trim()

    editingUserId.value = null

    window.dispatchEvent(
      new CustomEvent('toast', {
        detail: { type: 'success', message: 'User profile updated' },
      }),
    )
  } catch (error) {
    log.error('Save user error:', error)
    window.dispatchEvent(
      new CustomEvent('toast', {
        detail: { type: 'error', message: error instanceof Error ? error.message : 'Update failed' },
      }),
    )
  } finally {
    editSaving.value = false
  }
}

/**
 * Format a UTC date string to a short locale string.
 */
function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—'
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  } catch {
    return dateStr
  }
}

onMounted(() => {
  loadUsers()
})
</script>
